/** PAGES LÉGALES ET SUPPORT.
 *  Un pro qui confie sa clientèle à un outil doit pouvoir lire qui le gère,
 *  ce qu'on fait de ses données, et à qui parler quand ça coince. */
import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Instagram, Mail, MessageSquare } from "lucide-react";
import { Micro } from "../components/Bits";
import { Mot, Sceau } from "../components/Logo";

const EDITEUR = {
  marque: "Servae",
  email: "contact@servae.fr",
  support: "contact@servae.fr",
  instagram: "servae.app",
  delai: "1 jour ouvré",
  hebergeur: "Hébergement cloud sécurisé, infrastructure dans l'Union européenne",
  maj: "juin 2026",
};

function Cadre({ micro, titre, intro, chemin, children }) {
  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <Helmet>
        <title>{`${titre} — Servae`}</title>
        {intro && <meta name="description" content={intro} />}
        {chemin && <link rel="canonical" href={`https://servae.fr${chemin}`} />}
      </Helmet>
      <header className="ciel">
        <span className="nuage nuage-b derive-tres-lente" aria-hidden="true" />
        <span className="nuage nuage-d derive-lente" aria-hidden="true" />
        <div className="colonne pad pt-10 pb-12 relative">
          <Link to="/" className="inline-flex items-center gap-2.5" style={{ color: "var(--text)" }}>
            <Sceau taille={26} />
            <Mot hauteur={20} />
          </Link>
          <div className="mt-10">
            <Micro>{micro}</Micro>
            <h1 className="display text-[34px] md:text-[46px] mt-2 max-w-[24ch]">{titre}</h1>
            {intro && (
              <p className="text-[17px] mt-4 max-w-[60ch]" style={{ color: "var(--text-muted)" }}>
                {intro}
              </p>
            )}
          </div>
        </div>
      </header>
      <main className="colonne pad py-12 max-w-[760px]">{children}</main>
      <footer className="colonne pad pb-14">
        <div
          className="flex flex-wrap gap-x-6 gap-y-2 pt-6 text-[13.5px]"
          style={{ borderTop: "1px solid var(--border)", color: "var(--text-muted)" }}
        >
          <Link to="/mentions-legales">Mentions légales</Link>
          <Link to="/confidentialite">Confidentialité</Link>
          <Link to="/conditions">Conditions</Link>
          <Link to="/aide">Aide & contact</Link>
          <span style={{ color: "var(--text-subtle)" }}>
            Mise à jour : {EDITEUR.maj}
          </span>
        </div>
      </footer>
    </div>
  );
}

function Bloc({ titre, children }) {
  return (
    <section className="mb-10">
      <h2 className="display text-[23px] md:text-[26px] mb-3">{titre}</h2>
      <div className="space-y-3 text-[16px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
        {children}
      </div>
    </section>
  );
}

/* ------------------------------------------------- mentions légales */
export function MentionsLegales() {
  return (
    <Cadre
      micro="Informations légales"
      titre="Mentions légales"
      intro="Qui édite Servae, qui l'héberge, et comment nous joindre."
      chemin="/mentions-legales"
    >
      <Bloc titre="Éditeur du site">
        <p>
          Servae est édité par la société en cours d'immatriculation exploitant la marque{" "}
          <strong>Servae</strong>. Les informations d'immatriculation (forme juridique, capital,
          SIREN, RCS, TVA intracommunautaire) et l'adresse du siège seront publiées ici dès la
          finalisation de l'immatriculation, avant l'ouverture commerciale de janvier.
        </p>
        <p>
          Contact : <a href={`mailto:${EDITEUR.email}`} style={{ color: "var(--accent)" }}>{EDITEUR.email}</a>
        </p>
      </Bloc>
      <Bloc titre="Responsable de la publication">
        <p>Le représentant légal de la société éditrice.</p>
      </Bloc>
      <Bloc titre="Hébergement">
        <p>{EDITEUR.hebergeur}. Les données sont stockées dans l'Union européenne.</p>
      </Bloc>
      <Bloc titre="Propriété intellectuelle">
        <p>
          La marque Servae, le logo, l'interface, les textes et le code sont protégés. Toute
          reproduction sans autorisation écrite est interdite. Les contenus déposés par chaque
          professionnel (photos, prestations, fiches clients) restent sa propriété exclusive.
        </p>
      </Bloc>
      <Bloc titre="Signaler un contenu">
        <p>
          Servae héberge les pages de réservation de professionnels indépendants. Pour signaler un
          contenu illicite sur l'une de ces pages, écris à{" "}
          <a href={`mailto:${EDITEUR.support}`} style={{ color: "var(--accent)" }}>{EDITEUR.support}</a>{" "}
          en précisant l'adresse de la page concernée.
        </p>
      </Bloc>
    </Cadre>
  );
}

/* ------------------------------------------------- confidentialité */
export function Confidentialite() {
  return (
    <Cadre
      micro="Données personnelles"
      titre="Confidentialité"
      intro="Ta clientèle est ton seul actif. Elle ne sera jamais notre produit."
      chemin="/confidentialite"
    >
      <Bloc titre="Nos trois engagements">
        <p>
          <strong>1. Nous ne vendons rien.</strong> Aucune donnée n'est vendue, louée, ni cédée à
          un tiers, jamais, même agrégée.
        </p>
        <p>
          <strong>2. Nous ne démarchons pas tes clientes.</strong> Servae n'est pas un annuaire :
          tes clientes ne te seront jamais mis en concurrence avec un autre professionnel.
        </p>
        <p>
          <strong>3. Tu partiras quand tu veux, avec tout.</strong> Export complet en un clic,
          suppression définitive sur simple demande.
        </p>
      </Bloc>
      <Bloc titre="Responsable de traitement">
        <p>
          Pour ton compte professionnel, l'éditeur de Servae est responsable de traitement. Pour
          les fiches de tes clientes, <strong>tu es responsable de traitement</strong> et Servae
          agit comme sous-traitant, uniquement sur tes instructions.
        </p>
      </Bloc>
      <Bloc titre="Données traitées">
        <p>
          <strong>Toi :</strong> e-mail, mot de passe chiffré (jamais stocké en clair), nom
          d'activité, métier, téléphone, adresse, horaires, prestations, tarifs, données de
          facturation.
        </p>
        <p>
          <strong>Tes clientes :</strong> prénom, nom, mobile, e-mail si fourni, historique des
          rendez-vous, notes et photos que tu ajoutes. Le mobile sert de clé unique : il évite
          les fiches en double.
        </p>
        <p>
          <strong>Technique :</strong> journaux de connexion et d'erreur, conservés pour la
          sécurité et le diagnostic.
        </p>
      </Bloc>
      <Bloc titre="Finalités et bases légales">
        <p>
          Exécution du contrat : gérer tes rendez-vous, tes rappels, tes encaissements et ta page
          de réservation. Intérêt légitime : sécurité, prévention de la fraude, amélioration du
          service. Obligation légale : facturation et comptabilité.
        </p>
      </Bloc>
      <Bloc titre="Adresse et lien personnel">
        <p>
          Ton adresse exacte n'apparaît jamais sur ta page publique : seule la ville est affichée.
          Elle n'est transmise qu'à un client dont le rendez-vous est confirmé, via son lien
          personnel. Ce lien contient un identifiant aléatoire et ne donne accès qu'aux
          rendez-vous de ce client, à rien d'autre.
        </p>
      </Bloc>
      <Bloc titre="Sous-traitants">
        <p>
          Hébergement dans l'Union européenne. Envoi d'e-mails par un prestataire spécialisé.
          Assistance à la rédaction (relances, résumés) par un fournisseur de modèle
          de langage : les données envoyées sont réduites au minimum (prénom, prestation, dates)
          et ne servent jamais à entraîner un modèle. Paiement de l'abonnement par un prestataire
          certifié : nous ne voyons jamais ton numéro de carte.
        </p>
      </Bloc>
      <Bloc titre="Durées de conservation">
        <p>
          Compte actif : tant que ton abonnement est en cours. Après résiliation : 30 jours pour
          te permettre de revenir ou d'exporter, puis suppression. Pièces comptables : 10 ans,
          comme la loi l'exige. Journaux techniques : 12 mois.
        </p>
      </Bloc>
      <Bloc titre="Tes droits, et ceux de tes clientes">
        <p>
          Accès, rectification, effacement, limitation, opposition, portabilité. Écris à{" "}
          <a href={`mailto:${EDITEUR.support}`} style={{ color: "var(--accent)" }}>{EDITEUR.support}</a>{" "}
          : réponse sous 30 jours maximum. Un client peut aussi te demander directement la
          suppression de sa fiche, tu la supprimes depuis son profil. En cas de désaccord, un
          recours est possible auprès de la CNIL.
        </p>
      </Bloc>
      <Bloc titre="Cookies">
        <p>
          Servae n'utilise ni cookie publicitaire, ni traceur tiers, ni pixel de réseau social.
          Seul un stockage local conserve ta session pour éviter de te reconnecter à chaque
          ouverture. Aucune bannière à cliquer : il n'y a rien à consentir.
        </p>
      </Bloc>
    </Cadre>
  );
}

/* ------------------------------------------------- conditions */
export function Conditions() {
  return (
    <Cadre
      micro="Le contrat"
      titre="Conditions générales"
      intro="Ce que Servae te doit, ce que tu nous dois. En français lisible."
      chemin="/conditions"
    >
      <Bloc titre="Objet">
        <p>
          Servae est un logiciel en ligne de gestion de rendez-vous destiné aux professionnels
          indépendants des métiers de la beauté et du bien-être. Servae n'est ni un intermédiaire
          de paiement, ni une place de marché : aucune commission n'est prélevée sur ton chiffre
          d'affaires.
        </p>
      </Bloc>
      <Bloc titre="Essai, prix et facturation">
        <p>
          14 jours d'essai, sans carte bancaire. Ensuite 19,90 € par mois, ou 199 € par an. Prix
          TTC pour un professionnel établi en France. La facturation est mensuelle ou annuelle,
          d'avance, par prélèvement automatique.
        </p>
      </Bloc>
      <Bloc titre="Résiliation">
        <p>
          Sans engagement : tu résilies en un clic depuis ton compte, à tout moment. Le service
          reste actif jusqu'à la fin de la période déjà payée. Aucun remboursement au prorata,
          sauf en cas d'indisponibilité prolongée de notre fait.
        </p>
      </Bloc>
      <Bloc titre="Tes obligations">
        <p>
          Tu garantis avoir le droit de traiter les données de tes clientes, informer tes clientes
          de l'usage de Servae, et disposer des droits sur les photos que tu publies. Tu es
          responsable de tes tarifs, de tes prestations et de ta relation commerciale.
        </p>
      </Bloc>
      <Bloc titre="Disponibilité et responsabilité">
        <p>
          Nous visons une disponibilité maximale mais aucun service en ligne n'est infaillible.
          Les rappels par e-mail dépendent d'un prestataire tiers : leur remise ne peut pas
          être garantie. Notre responsabilité est limitée aux sommes versées sur les 12 derniers
          mois. Servae ne peut pas être tenu responsable d'un rendez-vous manqué, d'une absence de
          client ou d'une perte de chiffre d'affaires.
        </p>
      </Bloc>
      <Bloc titre="Tes données restent tes données">
        <p>
          Tu conserves la propriété entière de ta clientèle, de tes photos et de ton historique.
          Export complet possible à tout moment, même après résiliation, pendant 30 jours.
        </p>
      </Bloc>
      <Bloc titre="Évolution du service">
        <p>
          Servae évolue régulièrement. Toute modification substantielle des présentes conditions
          ou du tarif te sera annoncée par e-mail au moins 30 jours à l'avance. Si elle ne te
          convient pas, tu peux résilier sans frais.
        </p>
      </Bloc>
      <Bloc titre="Droit applicable">
        <p>
          Droit français. En cas de litige, nous cherchons d'abord une solution amiable : écris à{" "}
          <a href={`mailto:${EDITEUR.support}`} style={{ color: "var(--accent)" }}>{EDITEUR.support}</a>.
        </p>
      </Bloc>
    </Cadre>
  );
}

/* ------------------------------------------------- aide et support */
export function Aide() {
  const CANAUX = [
    {
      i: Mail,
      t: "L'assistance par e-mail",
      d: `Le canal principal. Réponse sous ${EDITEUR.delai} en semaine. Un souci le jour d'un rendez-vous ? Écris « URGENT » dans l'objet, ces messages passent devant.`,
      a: `mailto:${EDITEUR.support}`,
      l: EDITEUR.support,
    },
    {
      i: Instagram,
      t: "Instagram",
      d: "Pour une question rapide, ou juste pour nous dire ce qui manque.",
      a: `https://instagram.com/${EDITEUR.instagram}`,
      l: `@${EDITEUR.instagram}`,
    },
  ];

  const QUESTIONS = [
    ["Mon client n'a pas reçu son rappel.", "Vérifie d'abord que son e-mail est bien renseigné dans sa fiche, et demande-lui de regarder ses spams : c'est la cause la plus fréquente. En cas de doute, son lien personnel affiche toujours le rendez-vous à jour."],
    ["J'ai supprimé un rendez-vous par erreur.", "Écris-nous dans la journée avec le prénom du client et la date : les suppressions sont réversibles pendant 30 jours."],
    ["Je veux récupérer tous mes clientes.", "Réglages › Exporter mes données. Tu reçois un fichier tableur de tes clientes et un fichier complet de ton compte, exploitable ailleurs, sans nous demander la permission."],
    ["Mon adresse est-elle visible par tout le monde ?", "Non. Ta page publique n'affiche que ta ville. L'adresse exacte n'est transmise qu'aux clients dont le rendez-vous est confirmé."],
    ["Je change de téléphone, je perds mes fiches ?", "Non, tout est sur ton compte. Tu te reconnectes, tout est là."],
    ["Je veux arrêter.", "Réglages › Abonnement › Résilier. Un clic, aucune question, aucune relance."],
  ];

  return (
    <Cadre
      micro="Aide & contact"
      titre="Un problème ? On répond."
      intro="Servae gère tes rendez-vous : quand quelque chose coince, ça ne peut pas attendre trois jours."
      chemin="/aide"
    >
      <div className="grid sm:grid-cols-2 gap-3 mb-12">
        {CANAUX.map((c) => (
          <a
            key={c.t}
            href={c.a}
            data-testid={`canal-${c.t.slice(0, 12)}`}
            className="p-5 block"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-lg)",
            }}
          >
            <c.i size={19} strokeWidth={1.8} style={{ color: "var(--accent)" }} />
            <h3 className="text-[16px] font-semibold mt-3">{c.t}</h3>
            <p className="text-[14px] mt-1.5" style={{ color: "var(--text-muted)" }}>
              {c.d}
            </p>
            <p className="text-[14px] font-semibold mt-3" style={{ color: "var(--accent)" }}>
              {c.l}
            </p>
          </a>
        ))}
      </div>

      <Bloc titre="Les questions qui reviennent">
        <div style={{ borderTop: "1px solid var(--border)" }}>
          {QUESTIONS.map(([q, r]) => (
            <div key={q} className="py-4" style={{ borderBottom: "1px solid var(--border)" }}>
              <p className="text-[15.5px] font-semibold" style={{ color: "var(--text)" }}>
                {q}
              </p>
              <p className="text-[15px] mt-1.5">{r}</p>
            </div>
          ))}
        </div>
      </Bloc>

      <Bloc titre="Avant d'écrire, ce qui nous fait gagner du temps">
        <p>
          Dis-nous : ce que tu faisais, ce que tu attendais, ce qui s'est passé. Si tu peux, une
          capture d'écran. Et l'adresse e-mail de ton compte Servae.
        </p>
      </Bloc>
    </Cadre>
  );
}
